// ЧЕРНОВИК РЯДА И СЕТКА МЕСЯЦЕВ — арифметика ввода шага 2. Тикет B4-2
// `3e419875cbb8817da245c7a7484a9a7f`; правка ряда, укорачивание, undo и потолок — B4-3
// `3e419875cbb881b0b153c9d32d69bc36` (хвост файла: `restoreRow`, `spanMonths`, `pulledIn`,
// `shortenVictim` и запрет в `commitDraft`).
//
// ИСТОЧНИКИ. Ряд-черновик и его четыре состояния — борд `Life Map - s2 Draft Row Grid v2.html`
// (решение `s830.d1`); внутренность сетки — `Life Map - s2 Date Control v2.html` (`s824.d1`),
// и Draft Row Grid прямо говорит, что её не переписывает («внутренность без изменений от
// `Date Control v2`»). Живой канон экрана при этом остаётся `Stage Gate v5`: оттуда приходят
// префикс `started`, хвост `— until …`, `✓`/`✕` и микрокопи.
//
// ПОЧЕМУ ОТДЕЛЬНЫЙ МОДУЛЬ, А НЕ ТЕЛО КОМПОНЕНТА — та же причина, что у `stageList.js`: здесь
// утверждения о ДАННЫХ (какой месяц занят, куда встанет ряд, что печатает хвост), и проверять их
// надо без монтирования. Пять утверждений сетки из `_CC-INDEX.md` цепляются за разметку, но
// четыре из пяти держатся вот этими функциями.
//
// ⚠️ ЧАСЫ СЮДА НЕ ЗАХОДЯТ. «Сейчас» приходит из конверта (`session.nowY`, с месяцем по
// `s898.d1`) — ловушка `_CC-INDEX.md` названа там прямо: борд читает `new Date()`, ему можно,
// он мокап.

import { MAX_STAGES, STAGE_NAME_MAX } from '../../contracts.js';
import { durationLabel, endStartOf, monthsOf, nowMonths } from './stageList.js';

export { MAX_STAGES, STAGE_NAME_MAX };

/** Подписи месяцев сетки — байт борда (`Date Control v2`, ячейки `.mo span`). */
export const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

/** Пустой черновик нового этапа. Свежий объект на каждый вызов — общий литерал утёк бы. */
export function emptyDraft() {
  return { name: '', start: null, editing: null };
}

/**
 * Черновик из сохранённого ряда — то, что открывает `✎` (`s898.d2`).
 * Ряд при этом ОСТАЁТСЯ в `rows` со старыми значениями: счёт этапов и гейт не мигают.
 */
export function draftFromRow(rows, index) {
  const row = rows[index];
  if (!row) throw new Error(`правка ряда ${index}: такого ряда нет`);
  return { name: row.name || '', start: typeof row.start === 'string' ? row.start : null, editing: index };
}

/** Номер месяца → `MM/YYYY`. Обратная к `monthsOf`, и ровно тот формат, что хранят ряды. */
export function monthsToMMYYYY(months) {
  const y = Math.floor(months / 12);
  const m = months - y * 12 + 1;
  return `${String(m).padStart(2, '0')}/${y}`;
}

/** Год, в котором лежит номер месяца. */
export const yearOfMonths = (months) => Math.floor(months / 12);

/**
 * Занятые месяцы: старт КАЖДОГО ряда, кроме правимого (`s898.d2` + утверждение 5).
 *
 * Считается от ВСЕХ стартов, а не от соседа: утверждение 1 падает ровно на этой подмене —
 * «сетка 2019 содержит ровно одну занятую ячейку» верно только если смотреть весь ряд.
 * Значение — имя владеющего этапа: занятая ячейка обязана объяснять себя им (утверждение 2).
 *
 * @returns {Map<number, string>} номер месяца → имя этапа, который с него начинается
 */
export function occupiedMonths(rows, draft) {
  const skip = draft && Number.isInteger(draft.editing) ? draft.editing : -1;
  const out = new Map();
  rows.forEach((row, i) => {
    if (i === skip) return;
    const m = monthsOf(row && row.start);
    if (m !== null && !out.has(m)) out.set(m, (row && row.name) || '');
  });
  return out;
}

/**
 * Ряды без того, который правится (`s898.d2`). Для нового этапа — все.
 * Правимый ряд не участвует ни в одном сравнении: иначе он сравнивался бы сам с собой.
 */
export function rowsBesideDraft(rows, draft) {
  const skip = draft && Number.isInteger(draft.editing) ? draft.editing : -1;
  return rows.filter((_, i) => i !== skip);
}

/**
 * Куда встанет ряд с таким стартом — индекс в порядке ЭКРАНА (новый сверху).
 *
 * Это и есть вставка по дате вместо зажима борда (`s2 Stage Gate v5:423` · `if(m>prev-1)m=prev-1`,
 * `commit()` пушит в конец).
 *
 * @param {Array} kept ряды БЕЗ правимого — `rowsBesideDraft`
 */
export function placeByDate(kept, months) {
  let at = 0;
  while (at < kept.length) {
    const m = monthsOf(kept[at] && kept[at].start);
    if (m === null || m < months) break;
    at += 1;
  }
  return at;
}

/**
 * Конец черновика — старт того ряда, который окажется НАД ним, или `null` («сейчас»).
 *
 * ⚠️ ДВЕ ВЕТКИ, И РАЗНИЦА НЕ КОСМЕТИЧЕСКАЯ. С датой конец берётся ПО ДАТЕ — это названная
 * дивергенция `Draft Row Grid v2` от v5 («в v5 вставки нет, там черновик всегда самый старый и
 * `endTxt` = старт последнего ряда»). Без даты ставить ряд некуда, и печатается ровно то, что
 * печатает v5: старт самого старого ряда, а у первого этапа — «сейчас». Домысливать здесь
 * нечего: места у ряда ещё нет.
 */
export function draftEndStart(rows, draft) {
  const kept = rowsBesideDraft(rows, draft);
  if (kept.length === 0) return null;

  const months = monthsOf(draft && draft.start);
  if (months === null) return kept[kept.length - 1].start || null;

  const at = placeByDate(kept, months);
  return at === 0 ? null : (kept[at - 1].start || null);
}

/**
 * Что печатает ряд-черновик (борд `Draft Row Grid v2`, кадры 1–4).
 *
 * `duration` — только когда дата есть: без неё считать нечего, и `NaN` в строке дат был бы
 * хуже её отсутствия.
 */
export function draftRow(rows, draft, nowY) {
  const endStart = draftEndStart(rows, draft);
  const from = monthsOf(draft && draft.start);
  const to = endStart === null ? nowMonths(nowY) : monthsOf(endStart);

  return {
    name: (draft && draft.name) || '',
    start: (draft && draft.start) || null,
    end: endStart,
    duration: from === null ? null : durationLabel(from, to),
    complete: !!(draft && draft.name && draft.start),
  };
}

/** Тег черновика над рамкой (борд: `'stage · '+String(S.n+1).padStart(2,'0')+' · draft'`). */
export function draftTag(rows, draft) {
  const editing = draft && Number.isInteger(draft.editing) ? draft.editing : null;
  const n = editing === null ? rows.length + 1 : editing + 1;
  return `stage · ${String(n).padStart(2, '0')} · draft`;
}

/**
 * Перенести черновик в ряды по `✓` — ВСТАВКОЙ ПО ДАТЕ, а не в конец.
 *
 * Вставка по дате приземлена в B4-2 объявленным заходом: без неё приёмка «после любой
 * последовательности вводов старты строго возрастают» недостижима, а сетка ОБЯЗАНА отдавать
 * месяц в середине ряда (утверждение 1 прямо разбирает случай вставки). Видимая половина —
 * лейбл `end pulled in`, конфирм укорачивания, undo, `✎` — пришла в B4-3.
 *
 * ⭐ ПОТОЛОК ПРИНУЖДАЕТСЯ ЗДЕСЬ (B4-3, `s336.d4`). До этого тикета он был отчётом: счётчик писал
 * `15 · full`, `add` исчезал — но сама запись ряда шестнадцатым не мешала никому. Правка
 * (`draft.editing`) через потолок проходит всегда: она счёт не меняет, и запрет на ней означал бы
 * «на пятнадцати этапах даты больше не исправляются».
 *
 * @returns {{rows: Array, index: number}} новые ряды и место, на которое встал этап
 */
export function commitDraft(rows, draft) {
  if (!draft || !draft.name || !draft.start) {
    throw new Error('черновик без имени или даты в ряды не переносится — `✓` до этого не существует');
  }
  const months = monthsOf(draft.start);
  // Дату этапа в текст не кладём (`s946.d3`): сообщение уходит в Sentry.
  if (months === null) throw new Error('черновик несёт неразбираемую дату старта');
  if (!Number.isInteger(draft.editing) && rows.length >= MAX_STAGES) {
    throw new Error(`потолок ${MAX_STAGES} этапов взят: новый ряд не переносится — `
      + 'кнопки `add` на экране в этом состоянии нет вовсе');
  }

  const kept = rowsBesideDraft(rows, draft);
  const at = placeByDate(kept, months);
  const next = [...kept.slice(0, at), { name: draft.name, start: draft.start }, ...kept.slice(at)];
  return { rows: next, index: at };
}

/**
 * Вернуть удалённый ряд на место по дате (`s826.d2`, `undo` тоста).
 *
 * Берётся ТОТ ЖЕ объект ряда, а не его копия: имя и дата возвращаются ровно те, что были, и
 * пересборки значения из строки тоста не происходит. Место считает та же `placeByDate` —
 * у вернувшегося ряда нет своего «где он лежал», есть только дата.
 */
export function restoreRow(rows, row) {
  const months = monthsOf(row && row.start);
  if (months === null) throw new Error('вернуть ряд не на что: дата старта не разбирается');
  const at = placeByDate(rows, months);
  return [...rows.slice(0, at), row, ...rows.slice(at)];
}

/** Длительность ряда `i` В МЕСЯЦАХ, с тем же полом `1m`, что печатает `durationLabel`. */
export function spanMonths(rows, i, nowY) {
  const from = monthsOf(rows[i] && rows[i].start);
  const endStart = endStartOf(rows, i);
  const to = endStart === null ? nowMonths(nowY) : monthsOf(endStart);
  if (from === null || to === null) return null;
  return Math.max(1, to - from);
}

/**
 * Ряд, у которого правка ПОДТЯНУЛА выведенный конец (`s491.d5`, борд `Insertion Edges v2` 1·b).
 *
 * Лейбл живёт на СТАРШЕМ соседе, а не на новом ряду: новый ряд себя не объясняет — он просто
 * встал; объясняется тот, у кого изменилось то, чего он не вводил.
 *
 * Сравнение идёт ПО ТОЖДЕСТВУ объектов, а не по индексу: при вставке индексы съезжают у всех
 * рядов ниже, и сравнение по номеру назвало бы подтянутыми половину списка.
 *
 * @returns {{index: number, was: string|null}|null} место в `after`; `was` — прежний конец
 *   (`null` = «сейчас», у ряда это слово `present`)
 */
export function pulledIn(before, after) {
  // `null` (конец = «сейчас») и неразбираемая дата — оба «позже некуда»: о подтягивании они не
  // свидетельствуют, а бросать здесь не на чем — конверт правится руками.
  const at = (end) => (end === null ? Infinity : (monthsOf(end) ?? Infinity));

  const was = new Map();
  before.forEach((row, i) => was.set(row, endStartOf(before, i)));

  for (let i = 0; i < after.length; i += 1) {
    const row = after[i];
    if (!was.has(row)) continue;
    const old = was.get(row);
    if (at(endStartOf(after, i)) < at(old)) return { index: i, was: old };
  }
  return null;
}

/**
 * Порог конфирма укорачивания — АБСОЛЮТНЫЙ ПОЛ в месяцах (`s491.d2`).
 *
 * ⚠️ ПОРОГ СЧИТАЕТСЯ ОТ ТОГО, ЧТО ОСТАЛОСЬ, А НЕ ОТ ТОГО, СКОЛЬКО ПОТЕРЯНО. Подпись борда
 * `Insertion Edges v2` («теряет полгода — больше порога в 3 месяца», кадр 2·a, этап 5y 10m →
 * 5y 4m) читает порог вторым способом, и это отставание борда от решения, а не наша вольность:
 * s491.d2 называется «абсолютный пол», а «теряет больше половины длины» в нём УБРАНО прямым
 * текстом — «оно срабатывало бы почти на каждой вставке». Под чтением борда конфирм выскакивал
 * бы на любой вставке в длинный этап, то есть ровно в том случае, который решение исключило.
 * Расхождение вынесено строкой в `WIZARD-CANON.md`.
 */
export const SHORTEN_FLOOR = 3;

/**
 * Кого правка укоротила настолько, что надо спросить (`s491.d2`, борд, блок C).
 *
 * Три условия вместе, и каждое своё:
 *   · ряд ИМЕНОВАННЫЙ — безымянному терять нечего (борд: «безымянного черновика это не
 *     касается»). От визарда безымянный ряд не приходит, но конверт правится руками;
 *   · ряд СТАЛ КОРОЧЕ — удлинение не спрашивают;
 *   · того, что осталось, меньше `SHORTEN_FLOOR`.
 *
 * ⚠️ САМ ПРАВИМЫЙ (И НОВЫЙ) РЯД ЖЕРТВОЙ НЕ СЧИТАЕТСЯ, и это названный выбор сборки, а не
 * вычитка решения. Оно говорит про «этап, который становится короче», не оговаривая чей. Но
 * конфирм по смыслу борда — про последствие, которого человек не заказывал («конец, который он
 * не вводил»): дату правимого ряда он ставит руками сию секунду, и спрашивать «точно ли ты
 * хочешь то, что только что ввёл» — тот самый шум, против которого порог и заведён. Механически
 * это выражено тождеством: `commitDraft` кладёт НОВЫЙ объект, и его в `before` нет.
 *
 * @returns {{index: number, name: string, was: string, now: string}|null}
 */
export function shortenVictim(before, after, nowY) {
  const wasSpan = new Map();
  before.forEach((row, i) => wasSpan.set(row, spanMonths(before, i, nowY)));

  for (let i = 0; i < after.length; i += 1) {
    const row = after[i];
    if (!wasSpan.has(row) || !row.name) continue;
    const old = wasSpan.get(row);
    const now = spanMonths(after, i, nowY);
    if (old === null || now === null) continue;
    if (now < old && now < SHORTEN_FLOOR) {
      return { index: i, name: row.name, was: durationLabel(0, old), now: durationLabel(0, now) };
    }
  }
  return null;
}

/**
 * Состояние двенадцати ячеек года (борд `Date Control v2`, четыре кадра).
 *
 * Состояния поимённо, и каждое — своё утверждение теста:
 *   · `taken`  — месяц занят другим этапом, ячейка называет владельца (утв. 1, 2);
 *   · `future` — месяц за «сейчас», будущего в биографии нет (утв. 4);
 *   · `self`   — собственный месяц правимого ряда: НЕ запрещён, иначе дату не подтвердить (утв. 5);
 *   · `pick`   — выбранный месяц черновика;
 *   · `free`   — свободен.
 *
 * `disabled` выводится из состояния здесь, а не считается заново в разметке: иначе у запрета
 * появилось бы два определения.
 */
export function monthCells(year, { rows = [], draft = null, nowY } = {}) {
  const taken = occupiedMonths(rows, draft);
  const now = nowMonths(nowY);
  const picked = monthsOf(draft && draft.start);
  const own = draft && Number.isInteger(draft.editing)
    ? monthsOf(rows[draft.editing] && rows[draft.editing].start)
    : null;

  return MONTHS.map((label, i) => {
    const months = year * 12 + i;
    // ⚠️ `self` ПРОВЕРЯЕТСЯ ПЕРВЫМ, И ЭТО ПОРЯДОК БОРДА, А НЕ ВКУС. При правке черновик
    // открывается С ТЕКУЩЕЙ ДАТОЙ РЯДА, то есть `own === picked`, и кадр 4/4 рисует эту ячейку
    // «this one», а не янтарным выбором: человек ещё ничего не выбрал, ему показывают, где он
    // стоит. Проверь `pick` раньше — и подпись `s2.date.self` стала бы недостижимой.
    let state = 'free';
    if (months === own) state = 'self';
    else if (months === picked) state = 'pick';
    else if (taken.has(months)) state = 'taken';
    else if (now !== null && months > now) state = 'future';

    return {
      months,
      label,
      value: monthsToMMYYYY(months),
      state,
      owner: state === 'taken' ? taken.get(months) : null,
      disabled: state === 'taken' || state === 'future',
    };
  });
}

/**
 * Год, на котором сетка открывается.
 *
 * Порядок предпочтений — от самого определённого к самому общему: уже выбранный месяц → год
 * правимого ряда → год САМОГО СТАРОГО ряда (человек «start with now and work backwards», то
 * есть следующий этап он почти всегда ищет глубже) → год «сейчас» для самого первого этапа.
 */
export function openYear(rows, draft, nowY) {
  const picked = monthsOf(draft && draft.start);
  if (picked !== null) return yearOfMonths(picked);

  if (draft && Number.isInteger(draft.editing)) {
    const own = monthsOf(rows[draft.editing] && rows[draft.editing].start);
    if (own !== null) return yearOfMonths(own);
  }

  const kept = rowsBesideDraft(rows, draft);
  const oldest = monthsOf(kept.length ? kept[kept.length - 1].start : null);
  if (oldest !== null) return yearOfMonths(oldest);

  const now = nowMonths(nowY);
  if (now === null) {
    // Часы сюда не заходят (шапка файла), и подставить `new Date()` было бы ровно той ловушкой,
    // ради которой «сейчас» вообще вынесено в конверт. Годный `nowY` гарантирован ядром
    // (`acceptSession` бракует не-месяц как `corrupt`), поэтому это состояние недостижимо
    // из продукта и обязано быть громким в тесте, а не тихо открыть чужой год.
    throw new Error(`сетку не на чем открыть: nowY не называет месяц (${nowY})`);
  }
  return yearOfMonths(now);
}

/**
 * Можно ли листать на год вперёд: «год вперёд не листается» (борд, кадр 2/4 · `.anno`).
 * Будущего в биографии нет, и пустой год за «сейчас» был бы обещанием, что оно там есть.
 */
export function canStepYear(year, delta, nowY) {
  const next = year + delta;
  if (next < 1900) return false;
  const now = nowMonths(nowY);
  return now === null ? true : next <= yearOfMonths(now);
}
